import axios, { AxiosProgressEvent, HttpStatusCode } from "axios"
import { useAuthStore } from "@stores"
import { getFirstErrorFromResponse, appNavigate, isSignagePage } from "@utils/helpers"
import { ERROR_CODE, ROUTE, DEVICE_CODE_KEY } from "@constants"
import { ResponseBody } from "@/types"

const instance = axios.create({
  baseURL: process.env.API_URL,
  timeout: 60000,
  headers: {
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Methods": "*",
    "Access-Control-Expose-Headers": "*",
    "Cache-Control": "no-cache,no-store",
    "Content-Type": "application/json",
    Accept: "application/json",
  },
})

instance.interceptors.request.use((config) => {
  const token = useAuthStore.getState().access_token

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  if (isSignagePage()) {
    config.headers[DEVICE_CODE_KEY] = localStorage.getItem(DEVICE_CODE_KEY)
  }

  if (config.data instanceof FormData) {
    config.headers["Content-Type"] = "multipart/form-data"
  }

  return config
})

instance.interceptors.response.use(
  ({ data }) => data,
  (error) => {
    if (error.code === ERROR_CODE.ERR_NETWORK) {
      return Promise.reject(error)
    }

    const response = error.response?.data || {}
    const statusCode = response.code

    if (statusCode === HttpStatusCode.Unauthorized) {
      const resetToken = useAuthStore.getState()?.actions.resetToken

      resetToken()
    }

    if (isSignagePage()) {
      if (statusCode === HttpStatusCode.Forbidden || statusCode === HttpStatusCode.Unauthorized) {
        localStorage.setItem(DEVICE_CODE_KEY, "")
        appNavigate(ROUTE.SIGNAGE_PRODUCTS_TOP)
      }
    } else {
      switch (statusCode) {
        case HttpStatusCode.Unauthorized: {
          if (window.location.pathname !== ROUTE.LOGIN) {
            appNavigate(ROUTE.LOGIN)
          }

          break
        }
        case HttpStatusCode.Forbidden: {
          appNavigate(ROUTE.FORBIDDEN)
          break
        }
        case HttpStatusCode.NotFound: {
          const firstErrorMessage = getFirstErrorFromResponse(response?.errors)
          const message = firstErrorMessage || response.message || ""

          appNavigate(ROUTE.NOT_FOUND, { state: { message } })
          break
        }
        case HttpStatusCode.UnprocessableEntity: {
          break
        }
        default: {
          if (!error.config?.skipErrorRedirect) {
            appNavigate(ROUTE.INTERNAL_SERVER_ERROR)
          }

          break
        }
      }
    }

    return Promise.reject(response)
  }
)

export const validateStatus = (status: HttpStatusCode) => {
  return [HttpStatusCode.Ok, HttpStatusCode.Created, HttpStatusCode.Accepted].includes(status)
}

const httpRequests = {
  get: <Data, Params = {}>(endpoint: string, params?: Params) =>
    instance.get<ResponseBody<Data>>(endpoint, { params, validateStatus }) as unknown as Promise<ResponseBody<Data>>,
  post: <Data, RequestBody = {}>(endpoint: string, body?: RequestBody) =>
    instance.post<ResponseBody<Data>>(endpoint, body, { validateStatus }) as unknown as Promise<ResponseBody<Data>>,
  put: <Data, RequestBody = {}>(endpoint: string, body?: RequestBody) =>
    instance.put<ResponseBody<Data>>(endpoint, body, { validateStatus }) as unknown as Promise<ResponseBody<Data>>,
  patch: <Data, RequestBody = {}>(endpoint: string, body?: RequestBody) =>
    instance.patch<ResponseBody<Data>>(endpoint, body, { validateStatus }) as unknown as Promise<ResponseBody<Data>>,
  delete: <Data, Params = {}>(endpoint: string, params?: Params) =>
    instance.delete<ResponseBody<Data>>(endpoint, { params, validateStatus }) as unknown as Promise<ResponseBody<Data>>,
  upload: <Data, RequestBody = {}>(
    endpoint: string,
    body: RequestBody,
    configs?: {
      onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
      skipErrorRedirect?: boolean
    }
  ) =>
    instance.post<ResponseBody<Data>>(endpoint, body, { validateStatus, ...configs } as any) as unknown as Promise<
      ResponseBody<Data>
    >,
}

export default httpRequests
